/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DPC.POLICY — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DpcPolicyPage.tsx. That desktop page
 * configures three privacy artefacts in one screen; this file mirrors the same
 * data/logic layer with a mobile-native presentation (an entity switcher, dense
 * card lists, bottom-sheet detail + edit) — never the desktop grid re-hosted (§16):
 *   - Field masking rules   (dpc_field_rule)
 *   - Processing purposes   (dpc_purpose)
 *   - Subject data map/DSAR (dpc_subject_map)
 *
 * Reads go through apiDb; every write (activate/deactivate + scalar edits) routes
 * through the Common Routine via routedUpdate — §2 single write path, never a
 * direct apiDb write. Mirroring the reference mobile screens, destructive/create
 * operations and JSON-array authoring (unmask roles) stay on the desktop; the phone
 * edits the common scalars and toggles active. Masking strategy / erase-mode are
 * CHECK enums shown through i18n labelled constants (§17.B11), never bare literals;
 * unmask-role codes resolve to translated role names for display (§17.B4).
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, EyeOff, ListChecks, Fingerprint } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DPC.POLICY';

// ---------------------------------------------------------------------------
// Shared types + enum label constants (§17.B11 — the stored value is a code, the
// displayed label is localised; the allowed set is declared once, not inlined).
// ---------------------------------------------------------------------------

type Strategy = 'REDACT' | 'PARTIAL' | 'HASH' | 'NULLIFY';
type EraseMode = 'NULLIFY' | 'HASH' | 'REDACT';
type StatusFacet = 'all' | 'active' | 'inactive';
type Kind = 'rules' | 'purposes' | 'maps';

interface EnumOption<T extends string> { value: T; labelKey: string; fallback: string }

const STRATEGY_OPTIONS: readonly EnumOption<Strategy>[] = [
  { value: 'REDACT', labelKey: 'mobile.dpc.strategy.REDACT', fallback: 'Redact' },
  { value: 'PARTIAL', labelKey: 'mobile.dpc.strategy.PARTIAL', fallback: 'Partial' },
  { value: 'HASH', labelKey: 'mobile.dpc.strategy.HASH', fallback: 'Hash' },
  { value: 'NULLIFY', labelKey: 'mobile.dpc.strategy.NULLIFY', fallback: 'Nullify' },
];

const ERASE_MODE_OPTIONS: readonly EnumOption<EraseMode>[] = [
  { value: 'NULLIFY', labelKey: 'mobile.dpc.erase.NULLIFY', fallback: 'Nullify' },
  { value: 'HASH', labelKey: 'mobile.dpc.erase.HASH', fallback: 'Hash' },
  { value: 'REDACT', labelKey: 'mobile.dpc.erase.REDACT', fallback: 'Redact' },
];

type TFn = ReturnType<typeof useTranslation>['t'];

function enumLabel<T extends string>(t: TFn, options: readonly EnumOption<T>[], value: string): string {
  const opt = options.find((o) => o.value === value);
  return opt ? t(opt.labelKey, opt.fallback) : value;
}

/** Coerce a jsonb array column (unknown at the wire) to a clean string[]. */
function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string');
}

// ---------------------------------------------------------------------------
// Small shared presentation helpers
// ---------------------------------------------------------------------------

interface StatusFilterBarProps {
  query: string;
  onQuery: (v: string) => void;
  placeholder: string;
  facet: StatusFacet;
  onFacet: (f: StatusFacet) => void;
}

function StatusFilterBar({ query, onQuery, placeholder, facet, onFacet }: StatusFilterBarProps) {
  const { t } = useTranslation();
  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dpc.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];
  return (
    <>
      <div className="m-searchbox">
        <Search />
        <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
      </div>
      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => facet === v}
        onSelect={(v) => onFacet(v as StatusFacet)}
      />
    </>
  );
}

function StatusPill({ active }: { active: 'Y' | 'N' }) {
  const { t } = useTranslation();
  return (
    <MobileStatusPill tone={active === 'Y' ? 'ok' : 'no'}>
      {active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
    </MobileStatusPill>
  );
}

interface BottomSheetProps {
  open: boolean;
  label: string;
  onClose: () => void;
  closeDisabled?: boolean;
  children: ReactNode;
}

function BottomSheet({ open, label, onClose, closeDisabled, children }: BottomSheetProps) {
  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!closeDisabled) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={label}>
        <div className="m-grabber" />
        {children}
      </div>
    </>
  );
}

function ErrorCard({ message }: { message: string }) {
  return <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{message}</div></MobileContentCard>;
}

interface ToggleRowProps { title: string; hint: string; on: boolean; onToggle: () => void }

function ToggleRow({ title, hint, on, onToggle }: ToggleRowProps) {
  return (
    <div className="m-formrow m-formrow-inline">
      <span>
        <div className="m-formrow-l">{title}</div>
        <div className="m-formrow-s">{hint}</div>
      </span>
      <button type="button" role="switch" aria-checked={on} className={'m-switch ' + (on ? 'm-on' : 'm-off')} onClick={onToggle}>
        <span className="m-knob" />
      </button>
    </div>
  );
}

function statusMatches(facet: StatusFacet, active: 'Y' | 'N'): boolean {
  if (facet === 'active') return active === 'Y';
  if (facet === 'inactive') return active === 'N';
  return true;
}

// ===========================================================================
// Field masking rules — dpc_field_rule
// ===========================================================================

interface RuleRow {
  dpc_field_rule_id: number;
  entity_name: string;
  field_name: string;
  strategy: Strategy;
  partial_keep: number;
  unmask_roles: string[];
  is_active: 'Y' | 'N';
}

interface RoleTlRow { locale_code: string; display_text: string }

function RulesSection({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<RuleRow | null>(null);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dpc_field_rule')
        .select('dpc_field_rule_id, entity_name, field_name, strategy, partial_keep, unmask_roles, is_active')
        .order('entity_name');
      if (e) throw e;

      type Raw = Omit<RuleRow, 'unmask_roles'> & { unmask_roles: unknown };
      const rows: RuleRow[] = ((data ?? []) as Raw[]).map((r) => ({
        dpc_field_rule_id: r.dpc_field_rule_id,
        entity_name: r.entity_name,
        field_name: r.field_name,
        strategy: r.strategy,
        partial_keep: r.partial_keep,
        unmask_roles: toStringArray(r.unmask_roles),
        is_active: r.is_active,
      }));
      setRules(rows);

      // Resolve unmask-role codes to translated role names for display (§17.B4).
      const { data: rawRoles, error: rErr } = await apiDb
        .from('csm_role_master')
        .select('internal_code, csm_role_master_tl ( locale_code, display_text )');
      if (rErr) throw rErr;
      type RawRole = { internal_code: string; csm_role_master_tl?: RoleTlRow[] };
      const names: Record<string, string> = {};
      for (const role of (rawRoles ?? []) as RawRole[]) {
        const tls = role.csm_role_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        names[role.internal_code] = exact?.display_text ?? fb?.display_text ?? role.internal_code;
      }
      setRoleNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const roleLabel = (code: string) => roleNames[code] ?? code;
  const rolesText = (r: RuleRow) =>
    r.unmask_roles.length === 0 ? t('common.none', 'None') : r.unmask_roles.map(roleLabel).join(', ');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter((r) => {
      if (!statusMatches(statusFacet, r.is_active)) return false;
      if (!q) return true;
      return (
        r.entity_name.toLowerCase().includes(q) ||
        r.field_name.toLowerCase().includes(q) ||
        r.strategy.toLowerCase().includes(q)
      );
    });
  }, [rules, filter, statusFacet]);

  const toggleActive = async (r: RuleRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    const { error: e } = await routedUpdate(session, 'dpc_field_rule', 'dpc_field_rule_id', r.dpc_field_rule_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <>
      <StatusFilterBar
        query={filter}
        onQuery={setFilter}
        placeholder={t('mobile.dpc.rules.search', 'Search masking rules')}
        facet={statusFacet}
        onFacet={setStatusFacet}
      />

      {error && <ErrorCard message={error} />}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dpc.rules.none', 'No masking rules match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.dpc_field_rule_id}
              lead={<EyeOff />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={`${r.entity_name || t('mobile.dpc.rules.unnamed', 'Untitled rule')}${r.field_name ? ' · ' + r.field_name : ''}`}
              subtitle={`${enumLabel(t, STRATEGY_OPTIONS, r.strategy)} · ${rolesText(r)}`}
              trailing={<StatusPill active={r.is_active} />}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <BottomSheet open={selected !== null} label={t('mobile.dpc.rules.detail', 'Masking rule')} onClose={() => setSelected(null)} closeDisabled={actionBusy}>
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><EyeOff size={20} /></span>
              <span>
                <div className="m-nm">{selected.entity_name || t('mobile.dpc.rules.unnamed', 'Untitled rule')}</div>
                <div className="m-em">{selected.field_name || t('mobile.dpc.rules.no_field', 'No field')}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dpc.rules.status', 'Status')} value={<StatusPill active={selected.is_active} />} />
              <MobileDetailRow label={t('mobile.dpc.rules.strategy', 'Strategy')} value={enumLabel(t, STRATEGY_OPTIONS, selected.strategy)} />
              {selected.strategy === 'PARTIAL' && (
                <MobileDetailRow label={t('mobile.dpc.rules.keep', 'Keep last')} value={selected.partial_keep} />
              )}
              <MobileDetailRow label={t('mobile.dpc.rules.unmask_roles', 'Unmask roles')} value={rolesText(selected)} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => { setEditing(selected); setSelected(null); }}>
                <SquarePen size={16} />{t('mobile.dpc.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </BottomSheet>

      <RuleEditSheet
        session={session}
        rule={editing}
        rolesText={editing ? rolesText(editing) : ''}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </>
  );
}

interface RuleEditSheetProps {
  session: AuthSession;
  rule: RuleRow | null;
  rolesText: string;
  onClose: () => void;
  onSaved: () => void;
}

function RuleEditSheet({ session, rule, rolesText, onClose, onSaved }: RuleEditSheetProps) {
  const { t } = useTranslation();
  const [entityName, setEntityName] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [strategy, setStrategy] = useState<Strategy>('REDACT');
  const [partialKeep, setPartialKeep] = useState(4);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rule) return;
    setEntityName(rule.entity_name);
    setFieldName(rule.field_name);
    setStrategy(rule.strategy);
    setPartialKeep(rule.partial_keep);
    setActive(rule.is_active === 'Y');
    setError(null);
  }, [rule]);

  const save = async () => {
    if (!rule) return;
    if (!entityName.trim()) { setError(t('mobile.dpc.rules.entity_required', 'Entity is required.')); return; }
    if (!fieldName.trim()) { setError(t('mobile.dpc.rules.field_required', 'Field is required.')); return; }
    setBusy(true);
    setError(null);
    const { error: e } = await routedUpdate(session, 'dpc_field_rule', 'dpc_field_rule_id', rule.dpc_field_rule_id, {
      entity_name: entityName.trim(),
      field_name: fieldName.trim(),
      strategy,
      partial_keep: partialKeep,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  return (
    <BottomSheet open={rule !== null} label={t('mobile.dpc.rules.edit', 'Edit masking rule')} onClose={onClose} closeDisabled={busy}>
      {rule && (
        <>
          <div className="m-sheet-id">
            <span className="m-av"><EyeOff size={20} /></span>
            <span>
              <div className="m-nm">{t('mobile.dpc.rules.edit', 'Edit masking rule')}</div>
              <div className="m-em">{entityName || t('mobile.dpc.rules.unnamed', 'Untitled rule')}</div>
            </span>
          </div>

          <div className="m-fieldcard">
            <div className="m-formrow">
              <label htmlFor="m-dpc-rule-entity">{t('mobile.dpc.rules.entity', 'Entity')}</label>
              <input id="m-dpc-rule-entity" className="m-inp" value={entityName} onChange={(e) => setEntityName(e.target.value)} autoFocus />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-rule-field">{t('mobile.dpc.rules.field', 'Field')}</label>
              <input id="m-dpc-rule-field" className="m-inp" value={fieldName} onChange={(e) => setFieldName(e.target.value)} />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-rule-strategy">{t('mobile.dpc.rules.strategy', 'Strategy')}</label>
              <select id="m-dpc-rule-strategy" className="m-sel" value={strategy} onChange={(e) => setStrategy(e.target.value as Strategy)}>
                {STRATEGY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
              </select>
            </div>
            {strategy === 'PARTIAL' && (
              <div className="m-formrow">
                <label htmlFor="m-dpc-rule-keep">{t('mobile.dpc.rules.keep', 'Keep last')}</label>
                <input id="m-dpc-rule-keep" type="number" min={0} className="m-inp" value={partialKeep} onChange={(e) => setPartialKeep(Number(e.target.value))} />
              </div>
            )}
            <ToggleRow
              title={t('common.boolean.active', 'Active')}
              hint={t('mobile.dpc.rules.active_hint', 'Rule is enforced by the gateway')}
              on={active}
              onToggle={() => setActive((v) => !v)}
            />
            <div className="m-formrow">
              <label>{t('mobile.dpc.rules.unmask_roles', 'Unmask roles')}</label>
              <div className="m-formrow-ro">
                {rolesText}
                <span className="m-formrow-hint"> · {t('mobile.dpc.edit_desktop', 'edit on desktop')}</span>
              </div>
            </div>
          </div>

          {error && <ErrorCard message={error} />}

          <div className="m-actionbar">
            <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
            </button>
            <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
              <X size={16} />
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// ===========================================================================
// Processing purposes — dpc_purpose
// ===========================================================================

interface PurposeRow {
  dpc_purpose_id: number;
  internal_code: string;
  name: string;
  is_active: 'Y' | 'N';
}

function PurposesSection({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [purposes, setPurposes] = useState<PurposeRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<PurposeRow | null>(null);
  const [editing, setEditing] = useState<PurposeRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dpc_purpose')
        .select('dpc_purpose_id, internal_code, name, is_active')
        .order('internal_code');
      if (e) throw e;
      setPurposes((data ?? []) as PurposeRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return purposes.filter((p) => {
      if (!statusMatches(statusFacet, p.is_active)) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.internal_code.toLowerCase().includes(q);
    });
  }, [purposes, filter, statusFacet]);

  const toggleActive = async (p: PurposeRow) => {
    setActionBusy(true);
    setError(null);
    const next = p.is_active === 'Y' ? 'N' : 'Y';
    const { error: e } = await routedUpdate(session, 'dpc_purpose', 'dpc_purpose_id', p.dpc_purpose_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <>
      <StatusFilterBar
        query={filter}
        onQuery={setFilter}
        placeholder={t('mobile.dpc.purposes.search', 'Search purposes')}
        facet={statusFacet}
        onFacet={setStatusFacet}
      />

      {error && <ErrorCard message={error} />}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dpc.purposes.none', 'No purposes match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((p) => (
            <MobileCardRow
              key={p.dpc_purpose_id}
              lead={<ListChecks />}
              leadTone={p.is_active === 'Y' ? 'green' : 'slate'}
              title={p.name || p.internal_code}
              subtitle={p.internal_code}
              trailing={<StatusPill active={p.is_active} />}
              chevron
              onClick={() => setSelected(p)}
            />
          ))}
        </MobileContentCard>
      )}

      <BottomSheet open={selected !== null} label={t('mobile.dpc.purposes.detail', 'Processing purpose')} onClose={() => setSelected(null)} closeDisabled={actionBusy}>
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ListChecks size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dpc.purposes.status', 'Status')} value={<StatusPill active={selected.is_active} />} />
              <MobileDetailRow label={t('mobile.dpc.purposes.name', 'Name')} value={selected.name || t('common.none', 'None')} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => { setEditing(selected); setSelected(null); }}>
                <SquarePen size={16} />{t('mobile.dpc.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </BottomSheet>

      <PurposeEditSheet
        session={session}
        purpose={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </>
  );
}

interface PurposeEditSheetProps {
  session: AuthSession;
  purpose: PurposeRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function PurposeEditSheet({ session, purpose, onClose, onSaved }: PurposeEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!purpose) return;
    setName(purpose.name);
    setActive(purpose.is_active === 'Y');
    setError(null);
  }, [purpose]);

  const save = async () => {
    if (!purpose) return;
    if (!name.trim()) { setError(t('mobile.dpc.purposes.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    const { error: e } = await routedUpdate(session, 'dpc_purpose', 'dpc_purpose_id', purpose.dpc_purpose_id, {
      name: name.trim(),
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  return (
    <BottomSheet open={purpose !== null} label={t('mobile.dpc.purposes.edit', 'Edit purpose')} onClose={onClose} closeDisabled={busy}>
      {purpose && (
        <>
          <div className="m-sheet-id">
            <span className="m-av"><ListChecks size={20} /></span>
            <span>
              <div className="m-nm">{t('mobile.dpc.purposes.edit', 'Edit purpose')}</div>
              <div className="m-em">{purpose.internal_code}</div>
            </span>
          </div>

          <div className="m-fieldcard">
            <div className="m-formrow">
              <label>{t('mobile.dpc.purposes.code', 'Code')}</label>
              <div className="m-formrow-ro">
                {purpose.internal_code}
                <span className="m-formrow-hint"> · {t('mobile.dpc.edit_desktop', 'edit on desktop')}</span>
              </div>
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-purpose-name">{t('mobile.dpc.purposes.name', 'Name')}</label>
              <input id="m-dpc-purpose-name" className="m-inp" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <ToggleRow
              title={t('common.boolean.active', 'Active')}
              hint={t('mobile.dpc.purposes.active_hint', 'Available for consent capture')}
              on={active}
              onToggle={() => setActive((v) => !v)}
            />
          </div>

          {error && <ErrorCard message={error} />}

          <div className="m-actionbar">
            <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
            </button>
            <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
              <X size={16} />
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// ===========================================================================
// Subject data map (DSAR) — dpc_subject_map
// ===========================================================================

interface MapRow {
  dpc_subject_map_id: number;
  subject_entity: string;
  data_entity: string;
  match_column: string;
  pii_columns: string[];
  erase_mode: EraseMode;
  is_active: 'Y' | 'N';
}

function MapsSection({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [maps, setMaps] = useState<MapRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MapRow | null>(null);
  const [editing, setEditing] = useState<MapRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dpc_subject_map')
        .select('dpc_subject_map_id, subject_entity, data_entity, match_column, pii_columns, erase_mode, is_active')
        .order('subject_entity');
      if (e) throw e;
      type Raw = Omit<MapRow, 'pii_columns'> & { pii_columns: unknown };
      const rows: MapRow[] = ((data ?? []) as Raw[]).map((m) => ({
        dpc_subject_map_id: m.dpc_subject_map_id,
        subject_entity: m.subject_entity,
        data_entity: m.data_entity,
        match_column: m.match_column,
        pii_columns: toStringArray(m.pii_columns),
        erase_mode: m.erase_mode,
        is_active: m.is_active,
      }));
      setMaps(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return maps.filter((m) => {
      if (!statusMatches(statusFacet, m.is_active)) return false;
      if (!q) return true;
      return (
        m.subject_entity.toLowerCase().includes(q) ||
        m.data_entity.toLowerCase().includes(q) ||
        m.match_column.toLowerCase().includes(q)
      );
    });
  }, [maps, filter, statusFacet]);

  const toggleActive = async (m: MapRow) => {
    setActionBusy(true);
    setError(null);
    const next = m.is_active === 'Y' ? 'N' : 'Y';
    const { error: e } = await routedUpdate(session, 'dpc_subject_map', 'dpc_subject_map_id', m.dpc_subject_map_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const piiText = (m: MapRow) => (m.pii_columns.length === 0 ? t('common.none', 'None') : m.pii_columns.join(', '));

  return (
    <>
      <StatusFilterBar
        query={filter}
        onQuery={setFilter}
        placeholder={t('mobile.dpc.maps.search', 'Search subject map')}
        facet={statusFacet}
        onFacet={setStatusFacet}
      />

      {error && <ErrorCard message={error} />}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dpc.maps.none', 'No subject-map entries match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((m) => (
            <MobileCardRow
              key={m.dpc_subject_map_id}
              lead={<Fingerprint />}
              leadTone={m.is_active === 'Y' ? 'amber' : 'slate'}
              title={`${m.subject_entity || t('mobile.dpc.maps.unnamed', 'Untitled map')}${m.data_entity ? ' → ' + m.data_entity : ''}`}
              subtitle={`${m.match_column || t('mobile.dpc.maps.no_match', 'No match column')} · ${enumLabel(t, ERASE_MODE_OPTIONS, m.erase_mode)}`}
              trailing={<StatusPill active={m.is_active} />}
              chevron
              onClick={() => setSelected(m)}
            />
          ))}
        </MobileContentCard>
      )}

      <BottomSheet open={selected !== null} label={t('mobile.dpc.maps.detail', 'Subject data map')} onClose={() => setSelected(null)} closeDisabled={actionBusy}>
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Fingerprint size={20} /></span>
              <span>
                <div className="m-nm">{selected.subject_entity || t('mobile.dpc.maps.unnamed', 'Untitled map')}</div>
                <div className="m-em">{selected.data_entity || t('mobile.dpc.maps.no_data_entity', 'No data entity')}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dpc.maps.status', 'Status')} value={<StatusPill active={selected.is_active} />} />
              <MobileDetailRow label={t('mobile.dpc.maps.match_column', 'Match column')} value={selected.match_column || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.dpc.maps.pii_columns', 'PII columns')} value={piiText(selected)} />
              <MobileDetailRow label={t('mobile.dpc.maps.erase_mode', 'Erase mode')} value={enumLabel(t, ERASE_MODE_OPTIONS, selected.erase_mode)} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => { setEditing(selected); setSelected(null); }}>
                <SquarePen size={16} />{t('mobile.dpc.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </BottomSheet>

      <MapEditSheet
        session={session}
        entry={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </>
  );
}

interface MapEditSheetProps {
  session: AuthSession;
  entry: MapRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MapEditSheet({ session, entry, onClose, onSaved }: MapEditSheetProps) {
  const { t } = useTranslation();
  const [subjectEntity, setSubjectEntity] = useState('');
  const [dataEntity, setDataEntity] = useState('');
  const [matchColumn, setMatchColumn] = useState('');
  const [piiCsv, setPiiCsv] = useState('');
  const [eraseMode, setEraseMode] = useState<EraseMode>('NULLIFY');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    setSubjectEntity(entry.subject_entity);
    setDataEntity(entry.data_entity);
    setMatchColumn(entry.match_column);
    setPiiCsv(entry.pii_columns.join(', '));
    setEraseMode(entry.erase_mode);
    setActive(entry.is_active === 'Y');
    setError(null);
  }, [entry]);

  const save = async () => {
    if (!entry) return;
    if (!subjectEntity.trim()) { setError(t('mobile.dpc.maps.subject_required', 'Subject entity is required.')); return; }
    if (!dataEntity.trim()) { setError(t('mobile.dpc.maps.data_required', 'Data entity is required.')); return; }
    const piiColumns = piiCsv.split(',').map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    setError(null);
    const { error: e } = await routedUpdate(session, 'dpc_subject_map', 'dpc_subject_map_id', entry.dpc_subject_map_id, {
      subject_entity: subjectEntity.trim(),
      data_entity: dataEntity.trim(),
      match_column: matchColumn.trim(),
      pii_columns: piiColumns,
      erase_mode: eraseMode,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  return (
    <BottomSheet open={entry !== null} label={t('mobile.dpc.maps.edit', 'Edit subject map')} onClose={onClose} closeDisabled={busy}>
      {entry && (
        <>
          <div className="m-sheet-id">
            <span className="m-av"><Fingerprint size={20} /></span>
            <span>
              <div className="m-nm">{t('mobile.dpc.maps.edit', 'Edit subject map')}</div>
              <div className="m-em">{subjectEntity || t('mobile.dpc.maps.unnamed', 'Untitled map')}</div>
            </span>
          </div>

          <div className="m-fieldcard">
            <div className="m-formrow">
              <label htmlFor="m-dpc-map-subject">{t('mobile.dpc.maps.subject_entity', 'Subject entity')}</label>
              <input id="m-dpc-map-subject" className="m-inp" value={subjectEntity} onChange={(e) => setSubjectEntity(e.target.value)} autoFocus />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-map-data">{t('mobile.dpc.maps.data_entity', 'Data entity')}</label>
              <input id="m-dpc-map-data" className="m-inp" value={dataEntity} onChange={(e) => setDataEntity(e.target.value)} />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-map-match">{t('mobile.dpc.maps.match_column', 'Match column')}</label>
              <input id="m-dpc-map-match" className="m-inp" value={matchColumn} onChange={(e) => setMatchColumn(e.target.value)} />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-map-pii">{t('mobile.dpc.maps.pii_columns', 'PII columns')}</label>
              <input
                id="m-dpc-map-pii"
                className="m-inp"
                value={piiCsv}
                onChange={(e) => setPiiCsv(e.target.value)}
                placeholder={t('mobile.dpc.maps.pii_placeholder', 'comma-separated columns')}
              />
            </div>
            <div className="m-formrow">
              <label htmlFor="m-dpc-map-erase">{t('mobile.dpc.maps.erase_mode', 'Erase mode')}</label>
              <select id="m-dpc-map-erase" className="m-sel" value={eraseMode} onChange={(e) => setEraseMode(e.target.value as EraseMode)}>
                {ERASE_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
              </select>
            </div>
            <ToggleRow
              title={t('common.boolean.active', 'Active')}
              hint={t('mobile.dpc.maps.active_hint', 'Included in DSAR gather / erase')}
              on={active}
              onToggle={() => setActive((v) => !v)}
            />
          </div>

          {error && <ErrorCard message={error} />}

          <div className="m-actionbar">
            <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
            </button>
            <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
              <X size={16} />
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// ===========================================================================
// Page shell — an entity switcher over the three privacy artefacts.
// ===========================================================================

export function MobileDpcPolicyPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('rules');

  const tabs: { key: Kind; label: string }[] = [
    { key: 'rules', label: t('mobile.dpc.tab.rules', 'Masking') },
    { key: 'purposes', label: t('mobile.dpc.tab.purposes', 'Purposes') },
    { key: 'maps', label: t('mobile.dpc.tab.maps', 'Subject Map') },
  ];

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dpc.policy.title', 'Privacy Policy')}
        description={t('mobile.dpc.policy.description', 'Field masking, processing purposes and the subject data map for DSAR.')}
      />
      <MobileFilterChips
        chips={tabs.map((tab) => ({ value: tab.key, label: tab.label }))}
        isActive={(v) => kind === v}
        onSelect={(v) => setKind(v as Kind)}
      />

      {kind === 'rules' && <RulesSection session={session} />}
      {kind === 'purposes' && <PurposesSection session={session} />}
      {kind === 'maps' && <MapsSection session={session} />}
    </section>
  );
}
